"use client";

import { Fragment, useRef, useEffect, useState } from "react";
import { ChevronRightIcon } from "lucide-react";
import { LinkAnchor } from "../ui/link-anchor";
import {
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/app/components/ui/sheet";
import ContentSwitch from "../animations/content-switch";
import { ScrollableContainer } from "../ui/scrollable-container";
import { WiestLogo } from "../logos";
import { MENU_ITEMS, type MenuItemT } from "@/app/constants/menu-items";
import { getSafeSubmenuId } from "./submenu-id-utils";
import Link from "next/link";

type DesktopMenuProps = {
  menuItems?: MenuItemT[];
  activeSubmenu: MenuItemT | null;
  onMenuItemClick: (item: MenuItemT, e: React.MouseEvent) => void;
  activeNavLinkRef: React.MutableRefObject<HTMLAnchorElement | null>;
  onCloseSubmenu?: () => void;
  wiestLogoHref?: string;
  tenantDisplayName?: string;
};

export default function DesktopMenu({
  menuItems = MENU_ITEMS,
  activeSubmenu,
  onMenuItemClick,
  activeNavLinkRef,
  onCloseSubmenu,
  wiestLogoHref = "/",
  tenantDisplayName = "Wiest Group",
}: DesktopMenuProps) {
  const submenuSheetRef = useRef<HTMLDivElement>(null);
  const [activeNestedSubmenu, setActiveNestedSubmenu] =
    useState<MenuItemT | null>(null);
  const activeSubmenuId = activeSubmenu
    ? `desktop-submenu-${getSafeSubmenuId(activeSubmenu.href)}`
    : undefined;
  const activeNestedSubmenuId = activeNestedSubmenu
    ? `desktop-nested-submenu-${getSafeSubmenuId(activeNestedSubmenu.href)}`
    : undefined;

  useEffect(() => {
    setActiveNestedSubmenu(null);
  }, [activeSubmenu?.href]);

  // Setzen des Keyboard-Fokus auf das erste Submenu-Element
  useEffect(() => {
    if (!activeSubmenu || !submenuSheetRef.current) return;

    // Ersten Link im aktiven Submenu finden und fokussieren
    const submenuId = `desktop-submenu-${getSafeSubmenuId(activeSubmenu.href)}`;
    const submenuEl = document.getElementById(submenuId);
    if (!submenuEl) return;

    // Kurze Verzögerung, um DOM-Updates abzuwarten
    const focusFirstLink = () => {
      const firstLink = submenuEl.querySelector(
        'a[role="menuitem"]'
      ) as HTMLElement | null;
      if (firstLink) {
        firstLink.focus();
      }
    };

    const timer = setTimeout(focusFirstLink, 100);
    return () => clearTimeout(timer);
  }, [activeSubmenu]);

  // Event-Handler für Keyboard-Navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Tab-Navigation für Submenu (bestehende Logik)
      if (e.key === "Tab" && activeSubmenu) {
        const submenuId = `desktop-submenu-${getSafeSubmenuId(activeSubmenu.href)}`;
        const submenuEl = document.getElementById(submenuId);
        if (!submenuEl) return;

        // Alle fokussierbaren Elemente im Submenu finden
        const focusableElements = submenuEl.querySelectorAll(
          'a[href], button:not([disabled]), textarea:not([disabled]), input[type="text"]:not([disabled]), input[type="radio"]:not([disabled]), input[type="checkbox"]:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
        ) as NodeListOf<HTMLElement>;

        if (focusableElements.length === 0) return;

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        // Wenn wir das letzte Element erreichen und Tab drücken (vorwärts)
        if (!e.shiftKey && document.activeElement === lastElement) {
          e.preventDefault();
          // Submenu schließen
          if (onCloseSubmenu) {
            onCloseSubmenu();
          }
          // Fokus zurück zum aktiven Navlink setzen
          if (activeNavLinkRef.current) {
            activeNavLinkRef.current.focus();
          }
        }

        // Wenn wir das erste Element erreichen und Shift+Tab drücken (rückwärts)
        if (e.shiftKey && document.activeElement === firstElement) {
          e.preventDefault();
          // Submenu schließen
          if (onCloseSubmenu) {
            onCloseSubmenu();
          }
          // Fokus zurück zum aktiven Navlink setzen
          if (activeNavLinkRef.current) {
            activeNavLinkRef.current.focus();
          }
        }
        return;
      }

      // Pfeiltasten-Navigation
      if (
        e.key === "ArrowUp" ||
        e.key === "ArrowDown" ||
        e.key === "ArrowLeft" ||
        e.key === "ArrowRight"
      ) {
        // Prüfen, ob wir im Hauptmenü oder Submenu sind
        const activeElement = document.activeElement as HTMLElement;
        if (!activeElement) return;

        // Hauptmenü-Navigation (Pfeiltasten hoch/runter)
        const mainMenuLinks = document.querySelectorAll(
          'nav[aria-label="Hauptnavigation"] a[role="menuitem"]'
        ) as NodeListOf<HTMLAnchorElement>;

        if (mainMenuLinks.length > 0) {
          const currentIndex = Array.from(mainMenuLinks).findIndex(
            (link) => link === activeElement
          );

          if (
            currentIndex !== -1 &&
            (e.key === "ArrowUp" || e.key === "ArrowDown")
          ) {
            e.preventDefault();
            let nextIndex;

            if (e.key === "ArrowDown") {
              nextIndex = (currentIndex + 1) % mainMenuLinks.length;
            } else {
              nextIndex =
                currentIndex === 0
                  ? mainMenuLinks.length - 1
                  : currentIndex - 1;
            }

            mainMenuLinks[nextIndex].focus();
            return;
          }

          // Pfeil rechts: In Submenu wechseln, wenn verfügbar
          if (currentIndex !== -1 && e.key === "ArrowRight") {
            e.preventDefault();
            const currentMenuItem = mainMenuLinks[currentIndex];
            // Prüfen, ob das Element ein Submenu hat (Chevron-Icon)
            const hasSubmenu = currentMenuItem.querySelector(
              '[aria-hidden="true"]'
            );
            if (hasSubmenu) {
              // Klick simulieren, um Submenu zu öffnen
              currentMenuItem.click();
              // Kurze Verzögerung für DOM-Update, dann ersten Submenu-Link fokussieren
              setTimeout(() => {
                const newSubmenuId =
                  currentMenuItem.getAttribute("aria-controls");
                if (newSubmenuId) {
                  const submenuEl = document.getElementById(newSubmenuId);
                  if (submenuEl) {
                    const firstSubmenuLink = submenuEl.querySelector(
                      'a[role="menuitem"]'
                    ) as HTMLElement;
                    if (firstSubmenuLink) {
                      firstSubmenuLink.focus();
                    }
                  }
                }
              }, 150);
            }
            return;
          }
        }

        // Submenu-Navigation (Pfeiltasten hoch/runter für Items, links zum Hauptmenü)
        if (activeSubmenu) {
          const submenuId = `desktop-submenu-${getSafeSubmenuId(activeSubmenu.href)}`;
          const submenuEl = document.getElementById(submenuId);
          if (!submenuEl) return;

          const submenuLinks = submenuEl.querySelectorAll(
            'a[role="menuitem"]'
          ) as NodeListOf<HTMLAnchorElement>;

          if (submenuLinks.length > 0) {
            const currentIndex = Array.from(submenuLinks).findIndex(
              (link) => link === activeElement
            );

            // Navigation innerhalb des Submenus
            if (
              currentIndex !== -1 &&
              (e.key === "ArrowUp" || e.key === "ArrowDown")
            ) {
              e.preventDefault();
              let nextIndex;

              if (e.key === "ArrowDown") {
                nextIndex = (currentIndex + 1) % submenuLinks.length;
              } else {
                nextIndex =
                  currentIndex === 0
                    ? submenuLinks.length - 1
                    : currentIndex - 1;
              }

              submenuLinks[nextIndex].focus();
              return;
            }

            // Zurück zum Hauptmenü mit Pfeil links
            if (e.key === "ArrowLeft" && currentIndex !== -1) {
              e.preventDefault();
              // Submenu schließen
              if (onCloseSubmenu) {
                onCloseSubmenu();
              }
              // Fokus zurück zum aktiven Navlink setzen
              if (activeNavLinkRef.current) {
                activeNavLinkRef.current.focus();
              }
              return;
            }
          }
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [activeSubmenu, activeNavLinkRef, onCloseSubmenu]);

  return (
    <>
      <SheetTitle className="sr-only">Hauptmenü</SheetTitle>
      <SheetDescription className="sr-only">
        Navigation der Wiest Group mit Haupt- und Untermenüs
      </SheetDescription>

      {/* Desktop: Haupt-Menü */}
      <Link
        href={wiestLogoHref}
        className="absolute top-5 left-6 z-10 flex h-10 items-center"
        aria-label={`Zur Startseite von ${tenantDisplayName}`}
      >
        <WiestLogo className="h-8 w-auto" />
      </Link>

      <SheetHeader className="sr-only">
        <SheetTitle>Hauptmenü</SheetTitle>
        <SheetDescription>Wiest Group Hauptnavigation</SheetDescription>
      </SheetHeader>

      <nav aria-label="Hauptnavigation" className="pt-16">
        <ul className="mt-4 space-y-1" role="menu">
          {menuItems.map((item) => (
            <Fragment key={item.href}>
              {item.separatorBefore && (
                <li role="separator" aria-hidden="true" className="mt-6 mb-1">
                  <div className="text-muted-foreground mb-2 text-xs font-semibold tracking-wide">
                    Weitere Standorte
                  </div>
                </li>
              )}
              <li role="none">
                <LinkAnchor
                  className={`flex items-center justify-between px-2 py-2 text-lg font-semibold text-black ${
                    activeSubmenu?.href === item.href
                      ? "rounded bg-gray-100"
                      : ""
                  }`}
                  href={
                    item.children && item.children.length > 0 ? "#" : item.href
                  }
                  onClick={(e) =>
                    item.children &&
                    item.children.length > 0 &&
                    onMenuItemClick(item, e)
                  }
                  role="menuitem"
                  aria-haspopup={!!item.children}
                  aria-expanded={activeSubmenu?.href === item.href}
                  aria-controls={
                    item.children && activeSubmenu?.href === item.href
                      ? activeSubmenuId
                      : undefined
                  }
                  ref={
                    activeSubmenu?.href === item.href
                      ? activeNavLinkRef
                      : undefined
                  }
                >
                  {item.title}
                  {item.children && (
                    <ChevronRightIcon
                      className="size-5"
                      strokeWidth={1.5}
                      aria-hidden="true"
                    />
                  )}
                </LinkAnchor>
              </li>
            </Fragment>
          ))}
        </ul>
      </nav>

      {/* Desktop: Submenu erscheint rechts neben dem Hauptmenü */}
      {activeSubmenu && (
        <div
          className="absolute top-0 bottom-0 left-full w-[400px] overflow-visible border-l bg-white text-black"
          ref={submenuSheetRef}
          role="region"
          aria-labelledby={`${activeSubmenuId}-heading`}
          id={`desktop-submenu-${getSafeSubmenuId(activeSubmenu.href)}`}
        >
          <ContentSwitch
            id={`desktop-submenu-${getSafeSubmenuId(activeSubmenu.href)}`}
            direction="up"
            className="h-full"
          >
            <div className="flex h-full flex-col p-8 pt-20">
              <h2
                id={`${activeSubmenuId}-heading`}
                className="mb-2 text-lg font-semibold"
              >
                {activeSubmenu.title}
              </h2>

              <ScrollableContainer
                maxHeight="calc(100vh - 180px)"
                className=""
                containerClassName="flex-1"
              >
                <nav aria-label={`${activeSubmenu.title} Untermenü`}>
                  <ul className="space-y-2.5 pb-24" role="menu">
                    {activeSubmenu.children?.map((subItem, idx) => {
                      const hasChildren =
                        Array.isArray(subItem.children) &&
                        subItem.children.length > 0;
                      const isNestedSubmenuActive =
                        activeNestedSubmenu?.href === subItem.href;
                      return (
                        <li
                          key={`${subItem.href}-${subItem.title}-${idx}`}
                          role="none"
                        >
                          <LinkAnchor
                            className={`text-md inline-flex w-fit items-center justify-between gap-2 font-normal ${
                              isNestedSubmenuActive ? "rounded bg-gray-100" : ""
                            }`}
                            href={hasChildren ? "#" : subItem.href}
                            role="menuitem"
                            aria-haspopup={hasChildren ? true : undefined}
                            aria-expanded={
                              hasChildren ? isNestedSubmenuActive : undefined
                            }
                            aria-controls={
                              hasChildren && isNestedSubmenuActive
                                ? activeNestedSubmenuId
                                : undefined
                            }
                            onClick={(e) => {
                              if (!hasChildren) {
                                setActiveNestedSubmenu(null);
                                return;
                              }

                              e.preventDefault();
                              setActiveNestedSubmenu((current) =>
                                current?.href === subItem.href ? null : subItem
                              );
                            }}
                            {...(!hasChildren && subItem.external
                              ? {
                                  target: "_blank",
                                  rel: "noopener noreferrer",
                                  // Render as external link
                                  asNextLink: false,
                                }
                              : {})}
                          >
                            {subItem.title}
                            {hasChildren ? (
                              <ChevronRightIcon
                                className={`size-4 transition-transform ${
                                  isNestedSubmenuActive ? "rotate-90" : ""
                                }`}
                                strokeWidth={1.5}
                                aria-hidden="true"
                              />
                            ) : null}
                          </LinkAnchor>
                        </li>
                      );
                    })}
                  </ul>
                </nav>
              </ScrollableContainer>
            </div>
          </ContentSwitch>

          {activeNestedSubmenu && activeNestedSubmenu.children ? (
            <div
              className="absolute top-0 bottom-0 left-full w-[360px] border-l bg-white text-black"
              role="region"
              aria-labelledby={`${activeNestedSubmenuId}-heading`}
              id={activeNestedSubmenuId}
            >
              <ContentSwitch
                id={activeNestedSubmenuId!}
                direction="up"
                className="h-full"
              >
                <div className="flex h-full flex-col p-8 pt-20">
                  <h3
                    id={`${activeNestedSubmenuId}-heading`}
                    className="mb-2 text-lg font-semibold"
                  >
                    {activeNestedSubmenu.title}
                  </h3>

                  <ScrollableContainer
                    maxHeight="calc(100vh - 180px)"
                    className=""
                    containerClassName="flex-1"
                  >
                    <nav aria-label={`${activeNestedSubmenu.title} Untermenü`}>
                      <ul className="space-y-2.5 pb-24" role="menu">
                        {activeNestedSubmenu.children.map((nestedItem, idx) => (
                          <li
                            key={`${activeNestedSubmenu.href}-${nestedItem.href}-${idx}`}
                            role="none"
                          >
                            <LinkAnchor
                              className="text-md inline-flex w-fit font-normal"
                              href={nestedItem.href}
                              role="menuitem"
                              {...(nestedItem.external
                                ? {
                                    target: "_blank",
                                    rel: "noopener noreferrer",
                                    // Render as external link
                                    asNextLink: false,
                                  }
                                : {})}
                            >
                              {nestedItem.title}
                            </LinkAnchor>
                          </li>
                        ))}
                      </ul>
                    </nav>
                  </ScrollableContainer>
                </div>
              </ContentSwitch>
            </div>
          ) : null}
        </div>
      )}
    </>
  );
}
